import React, {useCallback, useEffect, useState} from "react";
import {useDispatch, useSelector} from 'react-redux';
import ClubFinderSearchBar from "../../elements/club-finder-search-bar/club-finder-search-bar";
import ClubFinderMapPage from "../../elements/club-finder-map-page/club-finder-map-page";
import {widgetConfigurationSliceActions} from "../../../store/slices/widgetConfigurationSlice";
import './club-finder-widget.scss';
import {RootState} from "../../../store/configureStore";
import {Config, AppVersionForTenants, TenantAppVersion, TenantAppVersionConfig} from "../../../constants/Config";
import axios from "axios";
import Loader from '../../atoms/Loader/loader';
import ClubFinderSearchBarVersion2 from "../../version-2/elements/club-finder-search-bar/club-finder-search-bar";
import {clubFinderSearchBarSliceActions} from "../../../store/slices/clubFinderSearchBarSlice";

interface ClubFinderWidgetProps {
  widgetProps: DOMStringMap;
  parentWidth: number;
  breakpointClass: string;
}

const baseClass = 'clubFinder-widget-main-container';
const ClubFinderWidget = (props: ClubFinderWidgetProps) => {
  const tenant = useSelector((state: RootState) => state.widgetConfigurationSettings.tenant);
  const dispatch = useDispatch();

  const [isLoading, setLoader] = useState(false);
  const formatDetails = useSelector((state: RootState) => state.widgetConfigurationSettings.formats);
  const genderOptions = useSelector((state: RootState) => state.widgetConfigurationSettings.genders);
  const daysOptions = useSelector((state: RootState) => state.widgetConfigurationSettings.days);
  const inclusionOptions = useSelector((state: RootState) => state.widgetConfigurationSettings.inclusions);
  const [widgetClassName, setWidgetClassName] = useState(tenant ? AppVersionForTenants[TenantAppVersion[tenant]].name : '')
  const [presetOrgId, setPresetOrgId] = useState('');

  const getWidgetConfiguration = useCallback(async () => {
    try {
      setLoader(true);
      if (props.widgetProps && props.widgetProps.finderId) {
        const apiUrl = `${Config.baseUrl}${Config.apiEndpoints.v1.configuration}?finderId=${props.widgetProps.finderId}`;
        await axios.get(apiUrl).then(async (resp) => {
          dispatch(widgetConfigurationSliceActions.setConfigurationSettings(resp.data));
        });
      }
      setLoader(false);
    } catch (e) {
      setLoader(false);
      console.log("Error in fetching configuration details for widget");
    }
  }, [dispatch, props.widgetProps])

  useEffect(() => {
    dispatch(widgetConfigurationSliceActions.setWidgetParentWidth({parentWidth: props.parentWidth, breakpointClass: props.breakpointClass}));
  }, [dispatch, props.parentWidth, props.widgetProps]);

  useEffect(() => {
    getWidgetConfiguration();
  }, [getWidgetConfiguration])

  useEffect(() => {
    if (tenant) {
      setWidgetClassName(AppVersionForTenants[TenantAppVersion[tenant]].name);
    }
    if (AppVersionForTenants.v2.tenants.includes(tenant)) {
      // Check if the parameters exist and do something with them
      // Get the current URL
      const currentURL = new URL(window.location.href);

      // Call the function to extract parameters
      const extractedParams = extractURLParams(currentURL);

      // Save the extracted parameters into separate variables

      if (extractedParams.genders && extractedParams.genders.length > 0) {
        let selectedGenders = [];
        for (let i = 0; i < extractedParams.genders.length; i++) {
          if (extractedParams.genders[i].toUpperCase() === 'MALE' || extractedParams.genders[i].toUpperCase() === 'FEMALE') {
            selectedGenders.push({
              type: extractedParams.genders[i].toUpperCase(),
              name: extractedParams.genders[i].toUpperCase()
            })
          } else if (extractedParams.genders[i].toUpperCase() === 'ANYONE') {
            selectedGenders.push(Config.defaultGenderOption)
          }
        }
        dispatch(clubFinderSearchBarSliceActions.setSavedMultiGenderFilter(selectedGenders))
      }

      if (extractedParams.formats && extractedParams.formats.length > 0) {
        const matchingFormats = formatDetails.filter((format: any) => format.playHqFormats.some((playFormat: any) => extractedParams.formats.includes(playFormat)));

        const namesOfMatchingFormats = matchingFormats.map((format: any) => format.name);
        dispatch(clubFinderSearchBarSliceActions.setSavedFormatFilter({
          selectedFormatObject: extractedParams.formats,
          selectedFormatTitles: namesOfMatchingFormats
        }))
      }

      if (extractedParams.days && extractedParams.days.length) {
        dispatch(clubFinderSearchBarSliceActions.setSavedDayFilter(extractedParams.days));
      }

      if (extractedParams.inclusions && extractedParams.inclusions.length) {
        const matchingInclusions = inclusionOptions.filter((inclusion: {
          inclusionDisplayValue: string,
          inclusionPlayHQValue: string
        }) => extractedParams.inclusions.includes(inclusion.inclusionPlayHQValue));

        dispatch(clubFinderSearchBarSliceActions.setSavedInclusionFilter(matchingInclusions));
      }

      if (extractedParams.orgId && extractedParams.orgId.length) {
        setPresetOrgId(extractedParams.orgId);
      }
    }
  }, [tenant])

  const extractURLParams = (url: any) => {
    const urlParams = new URLSearchParams(url.search);

    // Extract the "genders" parameter
    const genders = urlParams.get(Config.filterSearchParams[tenant as keyof typeof Config.filterSearchParams].genders)?.split(',');

    // Extract the "formats" parameter
    const formats = urlParams.get(Config.filterSearchParams[tenant as keyof typeof Config.filterSearchParams].formats)?.split(',');

    // Extract the "inclusions" parameter
    const inclusions = urlParams.get(Config.filterSearchParams[tenant as keyof typeof Config.filterSearchParams].inclusions)?.split(',');

    // Extract the "days" parameter
    const days = urlParams.get(Config.filterSearchParams[tenant as keyof typeof Config.filterSearchParams].days)?.split(',');

    // Extract the "orgId" parameter
    const orgId = urlParams.get(Config.filterSearchParams[tenant as keyof typeof Config.filterSearchParams].orgId);
    return {
      genders: genders || [],
      formats: formats || [],
      inclusions: inclusions || [],
      days: days || [],
      orgId
    };
  };

  return (
    <div className={`${baseClass} ${widgetClassName} ${tenant}`}
         style={
           {
             '--secondaryFont': tenant ? TenantAppVersionConfig[tenant].fonts.fontSecondary : '',
             '--primaryFont': tenant ? TenantAppVersionConfig[tenant].fonts.fontPrimary : '',
             '--brandPrimaryColour': tenant ? TenantAppVersionConfig[tenant].colours.brandPrimary : ''
           } as React.CSSProperties
         }
    >
      {props.widgetProps && props.widgetProps.finderId && !isLoading && tenant == 'nzc' && (
        <>
          <ClubFinderSearchBar formatDetails={formatDetails} genders={genderOptions}
                               clubFinderOffset={props.widgetProps.clubFinderOffset}/>
          <ClubFinderMapPage finderId={props.widgetProps.finderId}/>
        </>
      )}

      {AppVersionForTenants.v2.tenants.includes(tenant) && (
        <>
          <ClubFinderSearchBarVersion2 genderFilterOptions={genderOptions} inclusionFilterOptions={inclusionOptions}
                                       daysFilterOptions={daysOptions} formatFilterOptions={formatDetails}
                                       tenant={tenant}/>
          <ClubFinderMapPage finderId={props.widgetProps.finderId} presetOrgId={presetOrgId}/>
        </>
      )}


      {isLoading && (
        <>
          <Loader/>
        </>
      )}

      {(!props.widgetProps || props.widgetProps.finderId === null) && !isLoading && (
        <div>Please configure finder ID</div>
      )}
    </div>
  );
}

export default ClubFinderWidget;
